import { ActivatedRouteSnapshot, NavigationStart, Router } from "@angular/router";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, filter, map } from "rxjs";
import Swal, { SweetAlertResult } from "sweetalert2";
import { TenantService } from "./tenant.service";
import { Currency } from "../models/currency";
import { HttpErrorResponse } from "@angular/common/http";
import { CurrencyService } from "./currency.service";

@Injectable({
  providedIn: "root",
})
export class EventsService {
  private loadingContexts: { [key: string]: number } = {};
  private loadingCount$ = new BehaviorSubject<number>(0);

  public isLoading$: Observable<boolean> = this.loadingCount$.pipe(map((count) => count > 0));

  public readonly currency = new BehaviorSubject<Currency>(
    localStorage.getItem("currency")
      ? this.currencyService.getCurrencies().filter((c) => c?.id === localStorage.getItem("currency"))[0]
      : this.currencyService.getCurrencies()[0],
  );

  // azure has a different default currency which means different exchange rates
  // all azure reports should use the below and not the above
  public readonly azureCurrency = new BehaviorSubject<Currency>(
    localStorage.getItem("currency")
      ? this.currencyService
          .getCurrencies(undefined, "GBP")
          .filter((c) => c?.id === localStorage.getItem("currency"))[0]
      : this.currencyService.getCurrencies(undefined, "GBP")[0],
  );

  // drcc has a different default currency which means different exchange rates
  // all drcc reports should use the below and not the above
  public readonly drccCurrency = new BehaviorSubject<Currency>(
    localStorage.getItem("currency")
      ? this.currencyService
          .getCurrencies(undefined, "EUR")
          .filter((c) => c?.id === localStorage.getItem("currency"))[0]
      : this.currencyService.getCurrencies(undefined, "EUR")[0],
  );

  public readonly userFeedbackState = new BehaviorSubject<{ open: boolean; trigger: string }>({
    open: false,
    trigger: "",
  });
  public readonly sideFiltersState = new BehaviorSubject<boolean>(false);
  public readonly sideHelpState = new BehaviorSubject<boolean>(false);

  public readonly aiPopUpVisible = new BehaviorSubject<boolean>(false);
  public readonly gcpAnnouncementsPopUpVisible = new BehaviorSubject<boolean>(false);

  constructor(
    private tenant: TenantService,
    private currencyService: CurrencyService,
    private router: Router,
  ) {
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.reset(); // Clear all loading contexts
    });
  }

  private getTemplatedRoute(): string {
    const route = this.router.routerState.snapshot.url;
    return route ?? "default";
  }

  /**
   * NOTE: this MUST be used for displaying all errors to users in the whole application to ensure standardised format.
   *
   * @param e
   * @param {string} action e.g. 'Download Data', 'Load Data', 'Update Account Details', ...
   * @param {string} customMessage don't set it unless you have a very good reason to do so
   * @returns {Promise<SweetAlertResult<Awaited<unknown>>>}
   */
  public displayErrorToUser(
    e: Error | HttpErrorResponse,
    action: string,
    customMessage?: string,
  ): Promise<SweetAlertResult<Awaited<unknown>>> {
    return Swal.fire({
      icon: "error",
      confirmButtonColor: this.tenant.colors.primary,
      title: `Failed to ${action}`,
      text:
        customMessage ||
        ((e as HttpErrorResponse).error || {}).errorMessage ||
        ((e as HttpErrorResponse).error || {}).message ||
        e.message ||
        "Something went wrong.",
    });
  }

  public changeCurrency(currency: Currency): void {
    localStorage.setItem("currency", currency?.id);
    if (this.currency?.getValue()?.id !== currency?.id) {
      this.currency.next(currency);
    }
  }

  public changeCurrencyDrcc(currency: Currency): void {
    localStorage.setItem("currency", currency?.id);
    if (this.drccCurrency?.getValue()?.id !== currency?.id) {
      this.drccCurrency.next(currency);
    }
  }

  public changeCurrencyAzure(currency: Currency): void {
    localStorage.setItem("currency", currency?.id);
    if (this.azureCurrency?.getValue()?.id !== currency?.id) {
      this.azureCurrency.next(currency);
    }
  }

  public startLoading(context: string = this.getTemplatedRoute(), timeoutMs: number = 30000): void {
    this.loadingContexts[context] = (this.loadingContexts[context] || 0) + 1;
    this.updateLoadingCount();
    setTimeout(() => {
      this.stopLoading(context);
    }, timeoutMs);
  }

  public stopLoading(context: string = this.getTemplatedRoute()): void {
    if (this.loadingContexts[context]) {
      this.loadingContexts[context]--;
      if (this.loadingContexts[context] <= 0) {
        delete this.loadingContexts[context];
      }
      this.updateLoadingCount();
    }
  }

  public reset(): void {
    this.loadingContexts = {};
    this.loadingCount$.next(0);
  }

  private updateLoadingCount(): void {
    const total = Object.values(this.loadingContexts).reduce((sum, count) => sum + count, 0);
    this.loadingCount$.next(total);
  }
}
